import { FC, memo, useState } from 'react';

import NumberFormat from 'react-number-format';

import { Currencies } from '@constants/constants';
import { useBreakpoint } from '@contexts/breakpoints';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import Collapse from '@components/Collapse/Collapse';

import {
  CollapseTitle,
  ErrorMessage,
  PriceRangeButton,
  PriceRangeInputBox,
  PriceRangeInputLabel,
  PriceRangeInputWrapper,
  PriceRangeWrapper,
  PriceRangeContainer,
  StyledPriceRangeFilter,
  Title,
} from './PriceRangeFilterStyle';
import {
  INITIAL_MIN_MAX_ERROR_VALUES,
  MinMaxError,
  checkMinMaxValue,
} from './utils';

export interface PriceRangeFilterProps {
  minValue: number | undefined;
  maxValue: number | undefined;
  minMaxError?: MinMaxError;
  setMinValue: (val: number | undefined) => void;
  setMaxValue: (val: number | undefined) => void;
  handleSearch?: (a: number | undefined, b: number | undefined) => void;
  title: string;
  collapsible?: boolean;
}

const PriceRangeFilter: FC<PriceRangeFilterProps> = ({
  title,
  minValue,
  maxValue,
  minMaxError,
  setMinValue,
  setMaxValue,
  handleSearch,
  collapsible = false,
}) => {
  const screens = useBreakpoint();
  const [error, setError] = useState(INITIAL_MIN_MAX_ERROR_VALUES);

  const handleSearchClicked = (): void => {
    const controller = checkMinMaxValue(minValue || 0, maxValue || 0, error);
    if (!controller.minError && !controller.maxError && !controller.message) {
      handleSearch?.(minValue, maxValue);
    } else {
      setError(controller);
    }
  };

  useSkipEffectOnMount(() => {
    if (minMaxError) {
      setError(minMaxError);
    }
  }, [minMaxError]);

  const content = (
    <>
      <PriceRangeContainer>
        <PriceRangeWrapper>
          <PriceRangeInputWrapper isError={error.minError}>
            {minValue ? (
              <PriceRangeInputLabel>Min Fiyat</PriceRangeInputLabel>
            ) : null}

            <PriceRangeInputBox>
              <NumberFormat
                allowLeadingZeros={false}
                type='text'
                className='listing-filters-accommodation-input'
                value={minValue || ''}
                onValueChange={(values) => {
                  setMinValue(values.floatValue);
                }}
                maxLength={14}
                thousandSeparator={true}
                suffix={' ' + Currencies.TL}
                placeholder='Min Fiyat'
                size={14}
                onFocus={() => setError(INITIAL_MIN_MAX_ERROR_VALUES)}
              />
            </PriceRangeInputBox>
          </PriceRangeInputWrapper>

          <PriceRangeInputWrapper isError={error.maxError}>
            {maxValue ? (
              <PriceRangeInputLabel>Max Fiyat</PriceRangeInputLabel>
            ) : null}

            <PriceRangeInputBox>
              <NumberFormat
                allowLeadingZeros={false}
                type='text'
                className='listing-filters-accommodation-input'
                value={maxValue || ''}
                onValueChange={(values) => {
                  setMaxValue(values.floatValue);
                }}
                maxLength={14}
                thousandSeparator={true}
                suffix={' ' + Currencies.TL}
                placeholder='Max Fiyat'
                size={14}
                onFocus={() => setError(INITIAL_MIN_MAX_ERROR_VALUES)}
              />
            </PriceRangeInputBox>
          </PriceRangeInputWrapper>
        </PriceRangeWrapper>
        {error.message ? <ErrorMessage>{error.message}</ErrorMessage> : null}
      </PriceRangeContainer>

      {screens.xl ? (
        <PriceRangeButton onClick={handleSearchClicked}>Ara</PriceRangeButton>
      ) : null}
    </>
  );

  return collapsible ? (
    <Collapse
      defaultActiveKey='price-range-filter'
      panelProps={{
        header: <CollapseTitle>{title}</CollapseTitle>,
        key: 'price-range-filter',
      }}
    >
      <StyledPriceRangeFilter>{content}</StyledPriceRangeFilter>
    </Collapse>
  ) : (
    <StyledPriceRangeFilter>
      <Title>{title}</Title>
      {content}
    </StyledPriceRangeFilter>
  );
};

export default memo(PriceRangeFilter);
